import type { ShippingLabelData } from "../types/printer.types";

// ─── ESC/POS byte constants ───────────────────────────────────────────────────

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

// ─── Alignment values ─────────────────────────────────────────────────────────

const ALIGN = {
  LEFT: 0x00,
  CENTER: 0x01,
  RIGHT: 0x02,
} as const;

type AlignValue = (typeof ALIGN)[keyof typeof ALIGN];

// ─── Print width ──────────────────────────────────────────────────────────────

/** Max printable dots on a 58mm printer */
const PRINT_WIDTH_PX = 384;
/** Characters per line at normal font size */
const CHARS_PER_LINE = 32;
/** Padding in dots applied to each side of barcode and QR images */
const IMAGE_PADDING_PX = 16;

// ─── PNG → 1-bit bitmap converter ────────────────────────────────────────────

/**
 * Converts a data:image/png;base64,... string into RGBA pixel data
 * scaled to fit within maxWidth dots, preserving aspect ratio.
 */
export async function pngBase64ToPixels(
  dataUrl: string,
  maxWidth = PRINT_WIDTH_PX,
): Promise<{ pixels: Uint8Array; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.naturalWidth);
      const width = Math.floor(img.naturalWidth * scale);
      const height = Math.floor(img.naturalHeight * scale);

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Could not get 2D canvas context"));
        return;
      }

      // White background so transparent PNGs don't become solid black
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      const imageData = ctx.getImageData(0, 0, width, height);
      resolve({
        pixels: new Uint8Array(imageData.data.buffer),
        width,
        height,
      });
    };

    img.onerror = () => reject(new Error("Failed to load PNG image"));
    img.src = dataUrl;
  });
}

// ─── Pre-converted image data ─────────────────────────────────────────────────

export interface PreparedShippingImages {
  logo: { pixels: Uint8Array; width: number; height: number };
  barcode: { pixels: Uint8Array; width: number; height: number };
  qr: { pixels: Uint8Array; width: number; height: number };
}

/**
 * Pre-converts all 3 PNG images IN PARALLEL.
 * Call this as soon as label data arrives (page load / API response),
 * NOT inside the print button handler — so print is instant.
 */
export async function prepareShippingImages(
  label: ShippingLabelData,
): Promise<PreparedShippingImages> {
  const [logo, barcode, qr] = await Promise.all([
    pngBase64ToPixels(label.logoPngBase64, 200),
    pngBase64ToPixels(
      label.barcodePngBase64,
      PRINT_WIDTH_PX - IMAGE_PADDING_PX,
    ),
    pngBase64ToPixels(label.qrCodePngBase64, PRINT_WIDTH_PX - IMAGE_PADDING_PX),
  ]);
  return { logo, barcode, qr };
}

// ─── Builder class ────────────────────────────────────────────────────────────

class EscPosBuilder {
  private cmds: number[] = [];
  private readonly encoder = new TextEncoder();

  private push(...bytes: number[]): this {
    this.cmds.push(...bytes);
    return this;
  }

  private write(s: string): this {
    this.encoder.encode(s).forEach((b) => this.cmds.push(b));
    return this;
  }

  init(): this {
    return this.push(ESC, 0x40);
  }

  align(a: AlignValue): this {
    return this.push(ESC, 0x61, a);
  }

  bold(on: boolean): this {
    return this.push(ESC, 0x45, on ? 0x01 : 0x00);
  }

  /** Double width + double height text */
  doubleSize(on: boolean): this {
    return this.push(GS, 0x21, on ? 0x11 : 0x00);
  }

  line(s = ""): this {
    return this.write(s + "\n");
  }

  feed(n = 1): this {
    for (let i = 0; i < n; i++) this.push(LF);
    return this;
  }

  /** Full-width separator using = characters */
  doubleSeparator(): this {
    return this.line("=".repeat(CHARS_PER_LINE));
  }

  /** Full-width separator using - characters */
  singleSeparator(): this {
    return this.line("-".repeat(CHARS_PER_LINE));
  }

  /**
   * Two-column row padded to CHARS_PER_LINE.
   * If content overflows, right value wraps to next line right-aligned.
   */
  row(left: string, right: string): this {
    const gap = CHARS_PER_LINE - left.length - right.length;
    if (gap >= 1) {
      return this.line(left + " ".repeat(gap) + right);
    }
    return this.line(left).line(right.padStart(CHARS_PER_LINE));
  }

  /**
   * Renders a PNG (RGBA pixel data) as a 1-bit ESC/POS raster image.
   * Uses GS v 0 command.
   */
  image(pixels: Uint8Array, width: number, height: number): this {
    const byteWidth = Math.ceil(width / 8);

    this.push(
      GS,
      0x76,
      0x30,
      0x00,
      byteWidth & 0xff,
      (byteWidth >> 8) & 0xff,
      height & 0xff,
      (height >> 8) & 0xff,
    );

    for (let y = 0; y < height; y++) {
      for (let bx = 0; bx < byteWidth; bx++) {
        let byte = 0;
        for (let bit = 0; bit < 8; bit++) {
          const x = bx * 8 + bit;
          const idx = (y * width + x) * 4;
          if (x < width && pixels[idx + 3] > 128) {
            const lum =
              (pixels[idx] * 299 +
                pixels[idx + 1] * 587 +
                pixels[idx + 2] * 114) /
              1000;
            if (lum < 128) byte |= 1 << (7 - bit);
          }
        }
        this.push(byte);
      }
    }

    return this;
  }

  cut(): this {
    return this.push(GS, 0x56, 0x41, 0x03);
  }

  build(): Uint8Array {
    return new Uint8Array(this.cmds);
  }
}

// ─── Public builder ───────────────────────────────────────────────────────────

/**
 * Builds a full ESC/POS shipping label byte array.
 *
 * All images (logo, barcode, QR) are passed as data:image/png;base64,... strings
 * and are converted to 1-bit bitmaps via an offscreen <canvas> before encoding.
 *
 * @example
 * ```ts
 * const bytes = await buildShippingLabelBytes({
 *   logoPngBase64:    "data:image/png;base64,...",
 *   barcodePngBase64: "data:image/png;base64,...",
 *   qrCodePngBase64:  "data:image/png;base64,...",
 *   trackingNumber:   "RES0126150266",
 *   branchAddress:    "Jl. SM RAJA Medan",
 *   senderName:       "Sender Name",
 *   senderAddress:    "123 Warehouse Rd, Suite A\nMedan, North Sumatra",
 *   recipientName:    "John Doe",
 *   recipientAddress: "456 Main St\nJakarta, Indonesia",
 *   itemCount:        1,
 *   sendDate:         "10/06/2026",
 *   arrivalDate:      "11/06/2026",
 *   shippingCost:     40000,
 *   discount:         0,
 *   totalPayment:     0,
 *   paymentStatus:    "LUNAS",
 *   adminName:        "RIZKI PURBA",
 * });
 * ```
 */
export async function buildShippingLabelBytes(
  label: ShippingLabelData,
  images?: PreparedShippingImages,
): Promise<Uint8Array> {
  const fmt = (n: number) => "Rp " + n.toLocaleString("id-ID");
  const unit = label.itemUnit ?? "";

  // Use pre-converted images if provided, otherwise convert in parallel now
  const [logo, barcode, qr] = images
    ? [images.logo, images.barcode, images.qr]
    : await Promise.all([
        pngBase64ToPixels(label.logoPngBase64,    200),
        pngBase64ToPixels(label.barcodePngBase64, PRINT_WIDTH_PX - IMAGE_PADDING_PX),
        pngBase64ToPixels(label.qrCodePngBase64,  PRINT_WIDTH_PX - IMAGE_PADDING_PX),
      ]);

  const b = new EscPosBuilder();
  b.init();

  // ── Logo ─────────────────────────────────────────────────────────────────────
  b.align(ALIGN.CENTER).image(logo.pixels, logo.width, logo.height).feed();

  // ── COMPANY NAME ───────────────────────────────────────────────────────────
  b.feed().align(ALIGN.CENTER).line("PMH EKSPEDISI");

  // ── Branch address ───────────────────────────────────────────────────────────
  b.align(ALIGN.CENTER).line(label.branchAddress).feed();

  // ── Tracking number + barcode ────────────────────────────────────────────────
  b.doubleSeparator();

  b.align(ALIGN.LEFT)
    .bold(true)
    .line(`NOMOR RESI : ${label.trackingNumber}`)
    .bold(false);

  b.align(ALIGN.CENTER).image(barcode.pixels, barcode.width, barcode.height);

  b.doubleSeparator();

  // ── Sender ───────────────────────────────────────────────────────────────────
  b.align(ALIGN.LEFT).bold(true).line("PENGIRIM:").bold(false);

  label.senderName.split("\n").forEach((l) => b.line(l));
  label.senderAddress.split("\n").forEach((l) => b.line(l));

  // ── Separator ────────────────────────────────────────────────────────────────
  b.singleSeparator();

  // ── Recipient ────────────────────────────────────────────────────────────────
  b.bold(true).line("PENERIMA:").bold(false);
  label.recipientName.split("\n").forEach((l) => b.line(l));
  label.recipientAddress.split("\n").forEach((l) => b.line(l));

  // ── QR code ──────────────────────────────────────────────────────────────────
  b.doubleSeparator();

  b.align(ALIGN.LEFT).bold(true).line("QR CODE:").bold(false);

  b.align(ALIGN.CENTER).image(qr.pixels, qr.width, qr.height).feed();

  const qrLabel =
    label.qrLabel ?? "Scan QR Code di atas untuk lacak pengiriman";
  b.align(ALIGN.CENTER).line(qrLabel);

  b.doubleSeparator();

  // ── Shipment info ────────────────────────────────────────────────────────────
  b.align(ALIGN.LEFT)
    .line(`JUMLAH BARANG : ${label.itemCount} ${unit}`)
    .line(`TANGGAL KIRIM : ${label.sendDate}`)
    .line(`TANGGAL TIBA  : ${label.arrivalDate}`);

  b.doubleSeparator();

  // ── Payment info ─────────────────────────────────────────────────────────────
  b.align(ALIGN.LEFT)
    .line(`ONGKOS KIRIM  : ${fmt(label.shippingCost)}`)
    .line(`DISKON        : ${fmt(label.discount)}`)
    .bold(true)
    .line(`TOTAL BAYAR   : ${fmt(label.totalPayment)}`)
    .bold(false)
    .line(`STATUS BAYAR  : ${label.paymentStatus}`)
    .line(`NAMA ADMIN    : ${label.adminName}`);

  b.doubleSeparator();

  // ── Footer ───────────────────────────────────────────────────────────────────
  b.align(ALIGN.CENTER)
    .feed()
    .bold(true)
    .line("TERIMA KASIH")
    .bold(false)
    .line("SIMPAN STRUK INI SEBAGAI BUKTI")
    .feed(4)
    .cut();

  return b.build();
}
